import { IConfig, IEnv, IFirebaseClientCredentials } from './types'

const firebaseClientCredentials: IFirebaseClientCredentials = {
  apiKey: process.env.NEXT_PUBLIC_FIREBASE_API_KEY as string,
  authDomain: process.env.NEXT_PUBLIC_FIREBASE_AUTH_DOMAIN as string,
  projectId: process.env.NEXT_PUBLIC_FIREBASE_PROJECT_ID as string,
  storageBucket: process.env.NEXT_PUBLIC_FIREBASE_STORAGE_BUCKET as string,
  messagingSenderId: process.env
    .NEXT_PUBLIC_FIREBASE_MESSAGING_SENDER_ID as string,
  appId: process.env.NEXT_PUBLIC_FIREBASE_APP_ID as string
}

/**
 * Config — client-safe values only.
 *
 * Secrets (clientSecret, cronSecret, hygraph.apiKey, datadog.apiKey,
 * recaptcha.secretKey) live in `./server.ts` and are imported separately
 * by API routes / SSR helpers. This prevents accidental leakage via
 * `JSON.stringify(config)` in a server response.
 */

const env: IEnv = process.env.NEXT_PUBLIC_ENV as IEnv

const config: IConfig = {
  env,
  version: process.env.NEXT_PUBLIC_VERCEL_GIT_COMMIT_SHA as string,
  isProd: env === IEnv.Prod,
  isLocal: env === IEnv.Local,
  isServerSide: typeof window === 'undefined',
  baseUrl: process.env.NEXT_PUBLIC_BASE_URL as string,
  serverBaseUrl: process.env.NEXT_PUBLIC_SERVER_BASE_URL as string,
  graphqlUrl: process.env.NEXT_PUBLIC_GRAPHQL_URL as string,

  logger: {
    apiKey: process.env.NEXT_PUBLIC_LOG_FLARE_API_KEY as string,
    sourceToken: process.env.NEXT_PUBLIC_LOG_FLARE_SOURCE_TOKEN as string
  },

  datadog: {
    clientToken: process.env.NEXT_PUBLIC_DD_CLIENT_TOKEN as string
  },

  firebaseClientCredentials,
  googleWebClientId: process.env.NEXT_PUBLIC_GOOGLE_WEB_CLIENT_ID as string,
  googleMapsStaticKey: process.env.NEXT_PUBLIC_GOOGLE_MAPS_STATIC_KEY as string,
  recaptcha: {
    siteKey: process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY as string
  },
  /**
   * Firebase App Check — reCAPTCHA **v3 (classic)** site key.
   *
   * ABSENT BY DESIGN until the key exists: `firebaseClient.ts` skips App Check
   * entirely when this is unset, so shipping this costs nothing. Setting it
   * turns App Check on for the web app with NO further code change.
   *
   * Why we want it: the phone-OTP call goes browser -> identitytoolkit
   * .googleapis.com and never touches Vercel, so neither the firewall nor
   * BotID can see it (2026-08-24 SMS-pumping incident). App Check is the only
   * layer that can attest "this request came from OUR app" on that path.
   *
   * NOT the same key as `recaptcha.siteKey` — that one (6Ldd8rQm…) guards the
   * lead forms. This is a separate key dedicated to App Check; its paired
   * secret is registered on the Firebase App Check config, never in this repo.
   *
   * ROLLOUT ORDER MATTERS — enforcing before clients send tokens is a total
   * auth outage. See `spotme-server/src/scripts/setAppCheckEnforcement.ts`.
   */
  firebaseAppCheckSiteKey:
    process.env.NEXT_PUBLIC_FIREBASE_APPCHECK_SITE_KEY ?? '',

  pixels: {
    ga: env === IEnv.Prod ? 'AW-11007464981' : 'G-0LRWXEFLDH',
    // Microsoft Clarity removed 2026-05-13 in favor of PostHog (session
    // replay + product analytics + MCP). Bing UET (separate Microsoft
    // product, ad-conversion tracking) stays — see `uetq.ts`.
    fb: '797173148217215',
    posthog: 'phc_AI43NVkc8IxDGOYNwvByUXxy97NkfNSuPbpXHWKGqeL',
    // OpenAI / ChatGPT Ads (added 2026-08-21, risk-sleeve test). Loader in
    // constants/scripts.ts, wrapper in utils/pixels/oaiq.ts.
    openai: 'CgcXiNseA6uK2a3U3crTru'
  },

  hygraph: {
    url: process.env.NEXT_PUBLIC_HYGRAPH_CONTENT_URL as string
  },

  stripe: {
    publishableKey: process.env.NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY as string
  }
}

export default config
